import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';

const TermsPage = () => {
    return (
        <div className="min-h-screen bg-white py-20">
      <SEOHead page="terms" />
            <div className="max-w-4xl mx-auto px-6 lg:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8"
                >
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-slate-900 mb-4">Terms & Conditions</h1>
                        <p className="text-lg text-slate-600">Last updated: {new Date().toLocaleDateString()}</p>
                    </div>

                    <div className="prose prose-slate max-w-none space-y-8">
                        {/* Introduction */}
                        <div>
                            <p className="text-slate-600 mb-4">
                                These Terms and Conditions ("Terms," "Agreement," or "T&C") constitute a legally binding agreement between you ("User," "You," or "your") and digian.ai ("Company," "we," "us," or "our"). These Terms govern your access to and use of our website, services, and applications (collectively, "Services").
                            </p>
                            <p className="text-slate-600">
                                By accessing, registering for, or using digian.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions in their entirety. If you do not agree with any part of these Terms, you must not use our Services. We reserve the right to modify these Terms at any time, and continued use constitutes acceptance of changes.
                            </p>
                        </div>

                        {/* Acceptance of Terms */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
                            <p className="text-slate-600 mb-4">
                                Your use of digian.ai is conditioned upon your acceptance of and compliance with these Terms and Conditions. If you are accessing the Services on behalf of a company or organization, you represent and warrant that you have the authority to bind that entity to these Terms.
                            </p>
                            <p className="text-slate-600">
                                By using our Services, you:
                            </p>
                            <ul className="list-disc list-inside text-slate-600 space-y-2">
                                <li>Acknowledge that you have read and understood these Terms</li>
                                <li>Agree to comply with all applicable laws and regulations</li>
                                <li>Agree not to engage in activities that violate these Terms or any laws</li>
                                <li>Accept all risks associated with your use of the Services</li>
                            </ul>
                        </div>

                        {/* Service Description */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Service Description</h2>
                            <div className="space-y-4">
                                <p className="text-slate-600">
                                    digian.ai provides AI-powered data analytics and IT support solutions ("Services"). Our Services include:
                                </p>
                                <ul className="list-disc list-inside text-slate-600 space-y-2">
                                    <li>AI-powered analytics and data insights platform</li>
                                    <li>IT support and assistance services</li>
                                    <li>Reporting and visualization tools</li>
                                    <li>Integration with third-party platforms and services</li>
                                    <li>Customer support and documentation</li>
                                </ul>
                                <p className="text-slate-600 mt-4">
                                    The Services are provided on an "as is" and "as available" basis. We may modify, suspend, or discontinue any feature, functionality, or the entire Service at any time without notice. We are not liable for any modification, suspension, or discontinuation of Services.
                                </p>
                            </div>
                        </div>

                        {/* User Accounts */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Accounts and Responsibilities</h2>
                            <div className="space-y-4">
                                <p className="text-slate-600 mb-4">
                                    To access certain features of digian.ai, you may be required to create an account and provide accurate, current, and complete information.
                                </p>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Account Registration:</h3>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>You must provide truthful, accurate, and complete information during registration</li>
                                        <li>You are responsible for maintaining the confidentiality of your password and account credentials</li>
                                        <li>You agree to notify us immediately of any unauthorized access or breach of security</li>
                                        <li>You are liable for all activities that occur under your account</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">User Responsibilities:</h3>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>Maintain the security of your account and credentials</li>
                                        <li>Use the Services only for lawful purposes</li>
                                        <li>Not attempt to gain unauthorized access to our systems</li>
                                        <li>Comply with all applicable laws and these Terms</li>
                                        <li>Provide accurate information and keep it updated</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Intellectual Property Rights */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Intellectual Property Rights</h2>
                            <div className="space-y-4">
                                <p className="text-slate-600 mb-4">
                                    All content, materials, software, code, algorithms, and intellectual property contained within digian.ai are the exclusive property of digian.ai or our licensors and are protected by copyright, trademark, and other intellectual property laws.
                                </p>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Our Rights:</h3>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>We retain all rights to the platform, features, functionality, and design</li>
                                        <li>We grant you a limited, non-exclusive, non-transferable license to use the Services for your personal or business needs</li>
                                        <li>You may not reverse engineer, decompile, or attempt to derive the underlying technology</li>
                                        <li>You may not create derivative works or adaptations of our Services</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Your Content:</h3>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>You retain ownership of any content you upload or submit ("User Content")</li>
                                        <li>You grant digian.ai a worldwide, royalty-free license to use your content to provide and improve the Services</li>
                                        <li>You represent that you have all necessary rights to grant this license</li>
                                        <li>You are responsible for ensuring your content does not violate any laws or third-party rights</li>
                                    </ul>
                                </div>
                                <p className="text-slate-600 mt-4">
                                    Unauthorized use of our intellectual property is strictly prohibited and may result in civil and criminal liability.
                                </p>
                            </div>
                        </div>

                        {/* Acceptable Use Policy */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Acceptable Use Policy</h2>
                            <p className="text-slate-600 mb-4">
                                You agree not to use the Services in any manner that:
                            </p>
                            <ul className="list-disc list-inside text-slate-600 space-y-2">
                                <li>Violates any applicable laws, regulations, or industry standards</li>
                                <li>Infringes upon intellectual property rights of third parties</li>
                                <li>Contains viruses, malware, or harmful code</li>
                                <li>Is harassing, defamatory, obscene, or otherwise offensive</li>
                                <li>Attempts to gain unauthorized access or compromise system security</li>
                                <li>Transmits spam, phishing attempts, or unsolicited communications</li>
                                <li>Impersonates or fraudulently represents another person or entity</li>
                                <li>Interferes with or disrupts the integrity or performance of the Services</li>
                                <li>Engages in fraudulent transactions or payment disputes</li>
                                <li>Resells or redistributes the Services without authorization</li>
                                <li>Violates the rights or privacy of other users</li>
                            </ul>
                            <p className="text-slate-600 mt-4">
                                Violation of this policy may result in immediate termination of your account and legal action.
                            </p>
                        </div>

                        {/* SMS Communications */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. SMS Communications and Consent</h2>
                            <div className="space-y-4">
                                <p className="text-slate-600 mb-4">
                                    By submitting your contact information to digian.ai or using our services, you consent to receive SMS messages related to account activity, service notifications, authentication messages (such as OTPs), scheduling updates, maintenance alerts, and other service-related communications.
                                </p>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">SMS Opt-Out Policy:</h3>
                                    <p className="text-slate-600 mb-2">
                                        You may opt out of receiving SMS messages at any time by replying STOP to any message. Once you opt out, you will no longer receive SMS communications, but you may continue to receive emails or other communications as required to support your service usage or contractual obligations.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Message Frequency and Rates:</h3>
                                    <p className="text-slate-600">
                                        Message frequency may vary depending on your interactions with digian.ai and your use of our services. Standard message and data rates may apply based on your mobile carrier's plan. You are responsible for any carrier charges incurred.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Service Availability */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Service Availability and Modifications</h2>
                            <p className="text-slate-600 mb-4">
                                While we strive to maintain high availability, we do not guarantee uninterrupted or error-free service. We reserve the right to:
                            </p>
                            <ul className="list-disc list-inside text-slate-600 space-y-2">
                                <li>Modify or discontinue any feature or functionality at any time</li>
                                <li>Perform scheduled or emergency maintenance</li>
                                <li>Update, patch, or upgrade the Services</li>
                                <li>Change pricing or billing structures with advance notice</li>
                                <li>Implement usage limits, access restrictions, or rate limiting</li>
                            </ul>
                            <p className="text-slate-600 mt-4">
                                We will make reasonable efforts to provide notice of significant changes, but emergency maintenance may occur without prior notice. We are not liable for any service interruptions or downtime.
                            </p>
                        </div>

                        {/* Limitation of Liability */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Limitation of Liability</h2>
                            <div className="space-y-4">
                                <p className="text-slate-600 mb-4">
                                    TO THE FULLEST EXTENT PERMITTED BY LAW, DIGIAN.AI AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICES, INCLUDING BUT NOT LIMITED TO:
                                </p>
                                <ul className="list-disc list-inside text-slate-600 space-y-2">
                                    <li>Loss of data, revenue, or profits</li>
                                    <li>Business interruption or lost business opportunity</li>
                                    <li>Loss of use, access, or performance</li>
                                    <li>Reputational harm or damage</li>
                                    <li>Any damages arising from errors, omissions, or inaccuracies in our Services</li>
                                </ul>
                                <p className="text-slate-600 mt-4">
                                    Except as expressly stated in these Terms, our total liability to you for all claims arising from your use of the Services shall not exceed the amount you paid to us in the twelve (12) months preceding the claim, or $100, whichever is greater. Some jurisdictions do not allow the exclusion or limitation of liability, so this limitation may not apply to you.
                                </p>
                            </div>
                        </div>

                        {/* Indemnification */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Indemnification</h2>
                            <p className="text-slate-600">
                                You agree to indemnify, defend, and hold harmless digian.ai and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising out of or related to:
                            </p>
                            <ul className="list-disc list-inside text-slate-600 space-y-2">
                                <li>Your use of the Services</li>
                                <li>Your violation of these Terms and Conditions</li>
                                <li>Your infringement of any third-party intellectual property rights</li>
                                <li>Your violation of any applicable laws or regulations</li>
                                <li>User Content you submit or share</li>
                                <li>Your negligence or intentional misconduct</li>
                            </ul>
                        </div>

                        {/* Termination Rights */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Termination Rights</h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Termination by You:</h3>
                                    <p className="text-slate-600">
                                        You may terminate your account at any time by logging into your account settings or by contacting support@digian.ai. Upon termination, your access to the Services will be revoked, and we may delete your data after a retention period in accordance with our Privacy Policy.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Termination by Us:</h3>
                                    <p className="text-slate-600 mb-2">
                                        We reserve the right to terminate or suspend your account immediately, without notice, if you:
                                    </p>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>Violate these Terms and Conditions</li>
                                        <li>Engage in illegal or fraudulent activities</li>
                                        <li>Breach our Acceptable Use Policy</li>
                                        <li>Engage in abusive or harassing behavior</li>
                                        <li>Fail to pay applicable fees or charges</li>
                                        <li>Pose a security risk or threat to our Services or other users</li>
                                    </ul>
                                </div>
                                <p className="text-slate-600 mt-4">
                                    Upon termination, all licenses and rights granted under these Terms will immediately cease.
                                </p>
                            </div>
                        </div>

                        {/* Dispute Resolution */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Dispute Resolution and Arbitration</h2>
                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Informal Resolution:</h3>
                                    <p className="text-slate-600">
                                        Before pursuing formal legal action, you agree to contact us at support@digian.ai to attempt to resolve any disputes informally. We will respond within 30 days of your complaint.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Binding Arbitration:</h3>
                                    <p className="text-slate-600 mb-2">
                                        Any dispute arising out of or related to these Terms and Conditions or the Services shall be resolved through binding arbitration conducted in accordance with the American Arbitration Association (AAA) rules. The arbitration shall be conducted:
                                    </p>
                                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                                        <li>By a single neutral arbitrator</li>
                                        <li>In the English language</li>
                                        <li>In a location mutually agreed upon by the parties or as determined by the arbitrator</li>
                                    </ul>
                                    <p className="text-slate-600 mt-2">
                                        Each party shall bear its own attorneys' fees and costs, and the parties shall share the arbitrator's fees equally.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Class Action Waiver:</h3>
                                    <p className="text-slate-600">
                                        You agree that any arbitration or legal proceeding shall be conducted on an individual basis and not as a class action, collective action, or representative action. You waive any right to participate in class actions against digian.ai.
                                    </p>
                                </div>
                                <p className="text-slate-600 mt-4">
                                    <strong>Exception:</strong> Intellectual property infringement claims may be brought in court.
                                </p>
                            </div>
                        </div>

                        {/* Governing Law */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Governing Law and Jurisdiction</h2>
                            <p className="text-slate-600 mb-4">
                                These Terms and Conditions shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law principles.
                            </p>
                            <p className="text-slate-600">
                                You agree to submit to the exclusive jurisdiction of the courts located in [Your Jurisdiction] for any legal proceedings that are not subject to arbitration. Both parties waive any objection to venue or claim of inconvenient forum.
                            </p>
                        </div>

                        {/* Entire Agreement */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Entire Agreement</h2>
                            <p className="text-slate-600 mb-4">
                                These Terms and Conditions, together with our Privacy Policy and any other policies or agreements referenced herein, constitute the entire agreement between you and digian.ai regarding the Services and supersede all prior negotiations, understandings, and agreements.
                            </p>
                            <p className="text-slate-600 mb-4">
                                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. We may revise these Terms at any time, and your continued use of the Services constitutes acceptance of the revised Terms.
                            </p>
                            <p className="text-slate-600">
                                No waiver of any provision or breach of these Terms shall be deemed a waiver of any other provision or breach.
                            </p>
                        </div>

                        {/* Changes to Terms */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Changes to These Terms</h2>
                            <p className="text-slate-600 mb-4">
                                We reserve the right to modify these Terms and Conditions at any time. Changes will be effective upon posting to our website, and your continued use of the Services constitutes acceptance of the modified Terms.
                            </p>
                            <p className="text-slate-600">
                                We will provide notice of material changes via email or through a prominent notice on our website. If you do not agree with the modified Terms, you must discontinue your use of the Services.
                            </p>
                        </div>

                        {/* Contact Information */}
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Contact Us</h2>
                            <p className="text-slate-600 mb-4">
                                If you have questions about these Terms and Conditions, need assistance, or wish to report violations, please contact us at:
                            </p>
                            <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                                <p className="text-slate-600"><strong>Email:</strong> <a href="mailto:contact@digian.ai" className="text-indigo-600 hover:text-indigo-800">contact@digian.ai</a></p>
                                <p className="text-slate-600"><strong>Website:</strong> <a href="https://digian.ai" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">digian.ai</a></p>
                                <p className="text-slate-600"><strong>Response Time:</strong> We will respond to inquiries within 30 days.</p>
                            </div>
                        </div>

                        {/* Acknowledgment */}
                        <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-200">
                            <p className="text-slate-700">
                                <strong>By using digian.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions in their entirety.</strong>
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default TermsPage;
